import { BarChartOutlined, ReloadOutlined } from '@ant-design/icons';
import { Card, Col, Empty, Row, Statistic, Tabs } from 'antd';
import ReactECharts from 'echarts-for-react';
import { useEffect, useState } from 'react';

import { fetchResultStatistics } from '@/apis';
import PageHeader from '@/components/Admin/PageHeader';
import StateRenderer from '@/components/Admin/StateRenderer';
import { MessageType } from '@/enums';
import { useApiRequest, useAppFeedback } from '@/hooks';
import type { ResultStatistics, ScoreDistribution } from '@/types/apis/admin';

const AdminResultsPage = () => {
  const [statistics, setStatistics] = useState<ResultStatistics | null>(null);
  const { showMessage } = useAppFeedback();

  const {
    loading,
    error: fetchError,
    execute: loadStatistics,
  } = useApiRequest(
    async () => {
      const response = await fetchResultStatistics();
      setStatistics(response.data);
    },
    {
      requestKey: 'admin-result-statistics',
      onError: (error) => {
        const errorMessage = '載入統計數據失敗，請稍後再試';
        showMessage(errorMessage, MessageType.Error);
        console.error('Error loading statistics:', error);
      },
    },
  );

  const error = fetchError ? '載入統計數據失敗，請稍後再試' : null;

  useEffect(() => {
    loadStatistics();
  }, []);

  // Industry distribution pie chart
  const getIndustryDistributionOption = () => {
    if (!statistics?.industryDistribution) return {};

    return {
      title: {
        text: '行業分布',
        left: 'center',
      },
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        top: 'middle',
      },
      series: [
        {
          name: '行業分布',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: true,
            formatter: '{b}: {d}%',
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 14,
              fontWeight: 'bold',
            },
          },
          data: statistics.industryDistribution.map((item) => ({
            value: item.count,
            name: item.industryName,
          })),
        },
      ],
    };
  };

  // SDG goals average score bar chart
  const getSdgGoalScoresOption = () => {
    if (!statistics?.sdgGoalScores) return {};

    const sortedScores = [...statistics.sdgGoalScores].sort(
      (a, b) => b.averageScore - a.averageScore,
    );

    return {
      title: {
        text: 'SDG 平均分數',
        left: 'center',
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: unknown) => {
          const data = (
            params as { name: string; value: number; dataIndex: number }[]
          )[0];
          return `${data.name}<br/>平均分數: ${data.value.toFixed(2)}<br/>回應數: ${sortedScores[data.dataIndex].totalResponses}`;
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        max: 5,
        axisLabel: {
          formatter: '{value}',
        },
      },
      yAxis: {
        type: 'category',
        data: sortedScores.map((item) => item.goalName),
        axisLabel: {
          interval: 0,
          fontSize: 10,
        },
      },
      series: [
        {
          name: '平均分數',
          type: 'bar',
          data: sortedScores.map((item) => item.averageScore),
          itemStyle: {
            color: (params: { value: number }) => {
              const score = params.value;
              if (score >= 4) return '#52c41a';
              if (score >= 3) return '#1890ff';
              if (score >= 2) return '#faad14';
              return '#f5222d';
            },
          },
          label: {
            show: true,
            position: 'right',
            formatter: '{c}',
            fontSize: 10,
          },
        },
      ],
    };
  };

  // Industry SDG comparison heatmap
  const getIndustrySdgComparisonOption = () => {
    if (
      !statistics?.industrySdgComparison ||
      statistics.industrySdgComparison.length === 0
    ) {
      return {};
    }

    // Get all unique SDG goals
    const allGoals = Array.from(
      new Set(
        statistics.industrySdgComparison.flatMap((ind) =>
          ind.sdgScores.map((s) => s.goalName),
        ),
      ),
    ).sort();

    // Get all industries
    const industries = statistics.industrySdgComparison.map(
      (ind) => ind.industryName,
    );

    // Create data matrix
    const data: [number, number, number][] = [];
    statistics.industrySdgComparison.forEach((industry, indIndex) => {
      industry.sdgScores.forEach((score) => {
        const goalIndex = allGoals.indexOf(score.goalName);
        if (goalIndex !== -1) {
          data.push([goalIndex, indIndex, score.averageScore]);
        }
      });
    });

    return {
      title: {
        text: '行業 × SDG 表現分析',
        left: 'center',
      },
      tooltip: {
        position: 'top',
        formatter: (params: { value: [number, number, number] }) => {
          return `${industries[params.value[1]]}<br/>${allGoals[params.value[0]]}<br/>平均分數: ${params.value[2].toFixed(2)}`;
        },
      },
      grid: {
        left: '15%',
        right: '5%',
        bottom: '15%',
        top: '10%',
      },
      xAxis: {
        type: 'category',
        data: allGoals,
        splitArea: {
          show: true,
        },
        axisLabel: {
          interval: 0,
          rotate: 45,
          fontSize: 9,
        },
      },
      yAxis: {
        type: 'category',
        data: industries,
        splitArea: {
          show: true,
        },
        axisLabel: {
          fontSize: 10,
        },
      },
      visualMap: {
        min: 0,
        max: 5,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: '0%',
        inRange: {
          color: ['#f5222d', '#faad14', '#1890ff', '#52c41a'],
        },
      },
      series: [
        {
          name: '平均分數',
          type: 'heatmap',
          data: data,
          label: {
            show: true,
            formatter: (params: { value: [number, number, number] }) =>
              params.value[2].toFixed(1),
            fontSize: 9,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      ],
    };
  };

  // Score distribution chart
  const getScoreDistributionOption = (distribution: ScoreDistribution) => {
    return {
      title: {
        text: distribution.goalName,
        left: 'center',
        textStyle: {
          fontSize: 14,
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: unknown) => {
          const data = (
            params as { name: string; value: number; dataIndex: number }[]
          )[0];
          return `${data.name}<br/>數量: ${data.value}<br/>比例: ${distribution.distribution[data.dataIndex].percentage.toFixed(1)}%`;
        },
      },
      xAxis: {
        type: 'category',
        data: distribution.distribution.map((item) => item.range),
      },
      yAxis: {
        type: 'value',
      },
      series: [
        {
          data: distribution.distribution.map((item) => item.count),
          type: 'bar',
          itemStyle: {
            color: '#1890ff',
          },
        },
      ],
      grid: {
        left: '10%',
        right: '10%',
        bottom: '10%',
        top: '15%',
      },
    };
  };

  // Company size charts
  const getCompanySizeOption = (
    data: { category: string; averageSdgScore: number; count: number }[],
    title: string,
  ) => {
    return {
      title: {
        text: title,
        left: 'center',
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: unknown) => {
          const data = params as { name: string; value: number }[];
          const barData = data[0];
          const lineData = data[1];
          return `${barData.name}<br/>企業數量: ${barData.value}<br/>平均 SDG 分數: ${lineData.value.toFixed(2)}`;
        },
      },
      legend: {
        data: ['企業數量', '平均 SDG 分數'],
        top: 'bottom',
      },
      xAxis: {
        type: 'category',
        data: data.map((item) => item.category),
        axisLabel: {
          interval: 0,
          rotate: 0,
        },
      },
      yAxis: [
        {
          type: 'value',
          name: '企業數量',
          position: 'left',
        },
        {
          type: 'value',
          name: '平均分數',
          position: 'right',
          max: 5,
        },
      ],
      series: [
        {
          name: '企業數量',
          type: 'bar',
          data: data.map((item) => item.count),
          itemStyle: {
            color: '#1890ff',
          },
        },
        {
          name: '平均 SDG 分數',
          type: 'line',
          yAxisIndex: 1,
          data: data.map((item) => item.averageSdgScore),
          itemStyle: {
            color: '#52c41a',
          },
          lineStyle: {
            width: 3,
          },
        },
      ],
      grid: {
        left: '10%',
        right: '10%',
        bottom: '15%',
        top: '15%',
      },
    };
  };

  // Completion trend chart
  const getCompletionTrendOption = () => {
    if (!statistics?.completionTrend) return {};

    return {
      title: {
        text: '完成趨勢（近 30 天）',
        left: 'center',
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: unknown) => {
          const data = (params as { axisValue: string; value: number }[])[0];
          return `${data.axisValue}<br/>完成數: ${data.value}`;
        },
      },
      xAxis: {
        type: 'category',
        data: statistics.completionTrend.map((item) => item.date),
        axisLabel: {
          interval: 'auto',
          rotate: 45,
        },
      },
      yAxis: {
        type: 'value',
      },
      series: [
        {
          data: statistics.completionTrend.map((item) => item.count),
          type: 'line',
          smooth: true,
          itemStyle: {
            color: '#1890ff',
          },
          areaStyle: {
            color: 'rgba(24, 144, 255, 0.2)',
          },
        },
      ],
      grid: {
        left: '10%',
        right: '10%',
        bottom: '20%',
        top: '15%',
      },
    };
  };

  const tabItems = [
    {
      key: 'overview',
      label: '總覽',
      children: (
        <div>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="總結果數"
                  value={statistics?.overview.totalResults || 0}
                  prefix={<BarChartOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="已完成問卷數"
                  value={statistics?.overview.totalCompleted || 0}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="平均完成時間"
                  value={statistics?.overview.averageCompletionTime || 0}
                  suffix="天"
                  precision={1}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card>
                {statistics?.industryDistribution &&
                statistics.industryDistribution.length > 0 ? (
                  <ReactECharts
                    option={getIndustryDistributionOption()}
                    style={{ height: '400px' }}
                  />
                ) : (
                  <Empty description="暫無行業分布數據" />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card>
                {statistics?.completionTrend &&
                statistics.completionTrend.length > 0 ? (
                  <ReactECharts
                    option={getCompletionTrendOption()}
                    style={{ height: '400px' }}
                  />
                ) : (
                  <Empty description="暫無完成趨勢數據" />
                )}
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: 'sdg-scores',
      label: 'SDG 分數',
      children: (
        <Card>
          {statistics?.sdgGoalScores && statistics.sdgGoalScores.length > 0 ? (
            <ReactECharts
              option={getSdgGoalScoresOption()}
              style={{ height: '800px' }}
            />
          ) : (
            <Empty description="暫無 SDG 分數數據" />
          )}
        </Card>
      ),
    },
    {
      key: 'industry-comparison',
      label: '行業比較',
      children: (
        <Card>
          {statistics?.industrySdgComparison &&
          statistics.industrySdgComparison.length > 0 ? (
            <ReactECharts
              option={getIndustrySdgComparisonOption()}
              style={{ height: '600px' }}
            />
          ) : (
            <Empty description="暫無行業比較數據" />
          )}
        </Card>
      ),
    },
    {
      key: 'score-distribution',
      label: '分數分布',
      children: (
        <div>
          <Row gutter={[16, 16]}>
            {statistics?.scoreDistribution &&
            statistics.scoreDistribution.length > 0 ? (
              statistics.scoreDistribution.map((dist) => (
                <Col xs={24} md={12} lg={8} key={dist.goalName}>
                  <Card>
                    <ReactECharts
                      option={getScoreDistributionOption(dist)}
                      style={{ height: '300px' }}
                    />
                  </Card>
                </Col>
              ))
            ) : (
              <Col span={24}>
                <Empty description="暫無分數分布數據" />
              </Col>
            )}
          </Row>
        </div>
      ),
    },
    {
      key: 'company-size',
      label: '企業規模分析',
      children: (
        <div>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card>
                {statistics?.companySizeByEmployees &&
                statistics.companySizeByEmployees.length > 0 ? (
                  <ReactECharts
                    option={getCompanySizeOption(
                      statistics.companySizeByEmployees,
                      '按員工數量分析',
                    )}
                    style={{ height: '400px' }}
                  />
                ) : (
                  <Empty description="暫無員工數量數據" />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card>
                {statistics?.companySizeByYears &&
                statistics.companySizeByYears.length > 0 ? (
                  <ReactECharts
                    option={getCompanySizeOption(
                      statistics.companySizeByYears,
                      '按營運年限分析',
                    )}
                    style={{ height: '400px' }}
                  />
                ) : (
                  <Empty description="暫無營運年限數據" />
                )}
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="問卷結果統計"
        description="查看和分析問卷的統計數據。"
      />

      <StateRenderer
        loading={loading}
        error={error}
        empty={!statistics}
        emptyMessage="尚未有任何統計數據"
        emptyIcon={<BarChartOutlined />}
        onRetry={loadStatistics}
      >
        {statistics && (
          <Tabs
            defaultActiveKey="overview"
            items={tabItems}
            tabBarExtraContent={
              <ReloadOutlined
                onClick={loadStatistics}
                style={{ cursor: 'pointer', fontSize: 16 }}
              />
            }
          />
        )}
      </StateRenderer>
    </>
  );
};

export default AdminResultsPage;
